import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useState, type ComponentProps, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  SUBSCRIPTION_STATUS_LABEL,
  ORG_STATUS_LABEL,
  type OrgStatus,
  type PlatformRole,
  type SubscriptionStatus,
  type UsageLine,
} from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pill } from '@/features/finance/pill';
import { usePlatformMe } from '@/features/auth/guards';
import { api, errorMessage } from '@/lib/api';
import { fmtDate, fmtDateTime, money } from '@/lib/format';
import { cn } from '@/lib/utils';

export const TZ = 'America/Sao_Paulo';
export const dt = (iso: string | null | undefined) => (iso ? fmtDateTime(iso, TZ) : '—');
export const dd = (iso: string | null | undefined) => (iso ? fmtDate(iso, TZ) : '—');
export const pct = (v: number | null | undefined) => (v == null ? '—' : `${(v * 100).toFixed(1).replace('.', ',')}%`);
/** micros (1e-6 de real) -> "R$ 0,00" */
export const microsMoney = (m: number) => money(Math.round(m / 10_000));

/** Permissões de tela por papel (a checagem real é no backend). */
export function usePlatformAbility() {
  const { data: role } = usePlatformMe();
  const r = (role ?? 'readonly') as PlatformRole;
  return {
    role: r,
    isOwner: r === 'owner',
    canModerate: r === 'owner' || r === 'support',
    canBilling: r === 'owner' || r === 'finance',
    canTickets: r === 'owner' || r === 'support',
    canRead: true,
  };
}

const SUB_TONE: Record<SubscriptionStatus, 'neutral' | 'good' | 'warn' | 'bad'> = {
  trialing: 'warn',
  active: 'good',
  past_due: 'bad',
  grace: 'warn',
  suspended: 'bad',
  canceled: 'neutral',
};
export function SubPill({ status }: { status: SubscriptionStatus | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">Sem assinatura</span>;
  return <Pill tone={SUB_TONE[status]}>{SUBSCRIPTION_STATUS_LABEL[status]}</Pill>;
}
export function OrgPill({ status }: { status: OrgStatus }) {
  return <Pill tone={status === 'active' ? 'good' : status === 'suspended' ? 'bad' : 'warn'}>{ORG_STATUS_LABEL[status]}</Pill>;
}

/** Select de filtro com opção "Todos". */
export function FilterSelect({
  label,
  value,
  onChange,
  options,
  allLabel = 'Todos',
}: {
  label: string;
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  options: { value: string; label: string }[];
  allLabel?: string;
}) {
  return (
    <Select value={value || '_all'} onValueChange={(v) => onChange(v === '_all' ? null : v)}>
      <SelectTrigger size="sm" className="w-44 max-w-full" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="_all">
          {label}: {allLabel}
        </SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Cartões de indicadores com linhas alinhadas (subgrid). */
export function Kpis({ items }: { items: { label: string; value: string; hint?: string; tone?: 'warn' | 'bad' }[] }) {
  return (
    <dl className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-4">
      {items.map((i) => (
        <div key={i.label} className="row-span-3 grid grid-rows-subgrid gap-0 rounded-lg border bg-card px-4 py-3.5">
          <dt className="text-xs text-muted-foreground">{i.label}</dt>
          <dd className={cn('mt-1 font-display text-3xl leading-none font-bold tabular break-words', i.tone === 'warn' && 'text-warning', i.tone === 'bad' && 'text-destructive')}>{i.value}</dd>
          <p className="mt-1 text-xs text-muted-foreground">{i.hint ?? ' '}</p>
        </div>
      ))}
    </dl>
  );
}

export function UsageBars({ usage }: { usage: UsageLine[] }) {
  return (
    <ul className="grid gap-3">
      {usage.map((u) => {
        const ratio = u.limit ? Math.min(1, u.used / u.limit) : 0;
        const over = u.limit != null && u.used >= u.limit;
        return (
          <li key={u.key} className="grid gap-1">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
              <span>{u.label}</span>
              <span className={cn('tabular text-muted-foreground', over && 'font-medium text-destructive')}>
                {u.used.toLocaleString('pt-BR')} / {u.limit == null ? 'ilimitado' : u.limit.toLocaleString('pt-BR')}
              </span>
            </div>
            <div
              role="progressbar"
              aria-label={u.label}
              aria-valuenow={u.used}
              aria-valuemin={0}
              aria-valuemax={u.limit ?? undefined}
              className="h-2 overflow-hidden rounded-full bg-muted"
            >
              <div className={cn('h-full rounded-full', over ? 'bg-destructive' : ratio > 0.8 ? 'bg-warning' : 'bg-primary')} style={{ width: u.limit == null ? '8%' : `${Math.max(2, ratio * 100)}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function Card({ title, description, actions, children, className }: { title?: string; description?: string; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('min-w-0 rounded-lg border bg-card p-4 sm:p-5', className)}>
      {(title || actions) && (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div>
            {title && <h2 className="font-semibold">{title}</h2>}
            {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** Botão que baixa um arquivo respeitando a query ativa. */
export function DownloadButton({ path, query, filename, label = 'Exportar planilha' }: { path: string; query?: Record<string, string | number | boolean | null | undefined>; filename: string; label?: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="outline"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await api.download(path, query, filename);
        } catch (e) {
          toast.error(errorMessage(e));
        } finally {
          setBusy(false);
        }
      }}
    >
      <Download className="size-4" aria-hidden /> {busy ? 'Exportando…' : label}
    </Button>
  );
}

export function useList<T>(key: string, path: string, query: object) {
  return useQuery({ queryKey: ['platform', key, query], queryFn: () => api.get<T>(path, query as Record<string, string>), placeholderData: (p) => p });
}

/** Rótulos da cobrança. */
export const INVOICE_LABEL: Record<string, string> = { pending: 'Pendente', paid: 'Paga', overdue: 'Vencida', refunded: 'Estornada', canceled: 'Cancelada' };
export const EVENT_LABEL: Record<string, string> = { received: 'Recebido', processed: 'Processado', failed: 'Falhou', ignored: 'Ignorado' };
export const INTERVAL_LABEL = { month: 'mês', year: 'ano' } as const;

/** Ação de linha de lista/tabela: só ícone, com `aria-label` e tooltip (o texto nunca fica visível). Repassa ref/props para servir de gatilho de diálogo. */
export function IconAction({ label, icon, variant = 'ghost', className, ...props }: { label: string; icon: ReactNode } & Omit<ComponentProps<typeof Button>, 'size' | 'children' | 'aria-label'>) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" size="icon" variant={variant} className={cn('shrink-0', className)} aria-label={label} {...props}>
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
